import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormulaireService } from 'src/app/saisie/formulaire.service';
import { CsvExportService } from 'src/app/shared/csv-export.service';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { SuiviDetail, SuiviSaisieService } from '../suivi-saisie.service';

@Component({
  selector: 'bis-suivi-detail-permanente',
  templateUrl: './suivi-detail-permanente.component.html',
  styleUrls: ['./suivi-detail-permanente.component.css']
})
export class SuiviDetailPermanenteComponent implements OnInit, OnDestroy {

  dataSource: MatTableDataSource<SuiviDetail> = new MatTableDataSource<SuiviDetail>([]);
  displayedColumns: string[] = ['typStruct', 'nomStruct', 'etape', 'niveauErreur', 'ielst'];
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  public suivis: Array<SuiviDetail> = [];
  private filterValeur: string;
  public formulaire: UntypedFormGroup;
  public subChangesFormulaire: Subscription;

  constructor(
    public suiviSaisieService: SuiviSaisieService,
    private formulaireService: FormulaireService,
    private router: Router,
    private csvExportService: CsvExportService
  ) {
    }
  
  private comptageReducer = (summary, suivi) => {
    const compteur = summary.get(suivi['niveauErreur']) || 0;
    summary.set(suivi['niveauErreur'], compteur + 1);
    return summary;
  }

  ngOnInit() {
    this.formulaire =  this.suiviSaisieService.formulaire;

    this.subChangesFormulaire = this.formulaire.valueChanges.pipe(debounceTime(100), distinctUntilChanged()).subscribe((valeur) => {
      console.log('Récupération du suivi...');
      // ajout d'un timeout pour essayer d'éviter l'affichage de l'alert
      // "Un problème est survenu lors de l'appel au serveur Java..." du
      // SpinnerService, alert affichée quand l'utilisateur enchaine trop vite
      // de multiples changements de modalité dans les filtres du suivi détaillé,
      // l'IHM ne parvenant plus à gérer ce flot de requêtes à l'API trop nombreux en même temps du suivi
      // détaillé et cela déclenche l'appel au spinner
      setTimeout(() => {
        this.majSuivi();
      }, 3000);
    });
    this.formulaire.updateValueAndValidity();

    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  public majSuivi(): void {
    this.suivis = [];
    /* avec WS */
      this.suiviSaisieService.getSuiviDetail$(this.etape.value, this.niveauErreur.value).subscribe(retour => {
        this.suivis.push(...retour);
        this.dataSource.data = this.suivis;
      });   

    /* Sans WS 
    const suiviJson: Array<{typStruct: string, nomStruct: string, etape: string, niveauErreur: string, ielst: string}> = [
      {typStruct: 'TJ', nomStruct: 'Nantes', etape : 'validé', niveauErreur: 'avec alertes', ielst: '00100033'},
      {typStruct: 'TJ', nomStruct: 'Rennes', etape : 'sauvegardé', niveauErreur: '	erreurs bloquantes', ielst: '00100034'}
    ];
    this.suivis = suiviJson;
    this.dataSource.data = this.suivis;  */
  }


  applyFilter(filterValue: string = '') {
    this.filterValeur = filterValue;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  public getTotal() {
    return this.suivis.length;
  }

  public getComptage() {
    return Array.from(this.suivis.reduce(this.comptageReducer, new Map()));
  }

  public exportCSV(): void {
    if (this.suivis.length === 0) {
      Swal.fire({
        title: "Attention",
        text: "Il n'y a pas de lignes à extraire!",
        icon: "info"
      })
    } else {
        // il y a des lignes à exporter
        this.csvExportService.exportToCsv(this.suivis, 'listeJuridictions');
    }
  }

  /** Charge une saisie sélectionnée */
  public charge(item: SuiviDetail): void {
    Swal.fire({
      title: 'Attention',
      html: 'Êtes-vous sûr de vouloir charger la saisie <br>' +  item.ielst + ' - ' + item.nomStruct + '? <br>' + 'Toute saisie en cours et non sauvegardée sera perdue.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'OK',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.value) {
            // On teste que la saisie a recharger n'est pas celle en cours
            // Sinon juste à rerouter sur saisie car questionnaire deja en mémoire
            // Sinon pas de questionnaire en memoire chargé donc on force le chargement du questionnaire demandé
            if (this.formulaireService.getRootForm()) {
              if (this.formulaireService.getRootForm().get('ielst').value !== item.ielst) {
                    // on recherche le nouveau questionnaire à charger
                    this.formulaireService.getRootFormByVariable([item.ielst]);
              } else {
                    // On navigue vers /saisie pour remettre le questionnaire en cours
                    this.router.navigate(['/saisie']);
              }
            } else {
                // on recherche le questionnaire demandé à charger
                this.formulaireService.getRootFormByVariable([item.ielst]);
            }
      }
    })
  }

  public recharger() {
    this.majSuivi();
    setTimeout(() => {
        this.applyFilter(this.filterValeur);
    }, 500);
  }

  get etape(): UntypedFormControl {
    return this.formulaire.get('etape') as UntypedFormControl;
  }

  get niveauErreur(): UntypedFormControl {
    return this.formulaire.get('niveauErreur') as UntypedFormControl;
  }

  ngOnDestroy() {
    if (this.subChangesFormulaire) {
      this.subChangesFormulaire.unsubscribe();
    }
  }

}

